// src/utils/time.ts

export function minutes(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "–";

  const totalMins = Math.round(seconds / 60);

  if (totalMins < 60) {
    return `${totalMins} min${totalMins === 1 ? "" : "s"}`;
  }

  const hrs = Math.floor(totalMins / 60);
  const mins = totalMins % 60;

  const hrLabel = `hr${hrs === 1 ? "" : "s"}`;
  const minLabel = `min${mins === 1 ? "" : "s"}`;

  if (mins === 0) {
    return `${hrs}${hrLabel}`;
  }

  return `${hrs}${hrLabel} ${mins}${minLabel}`;
}

/**
 * Precise human duration:
 * - 38  -> "38s"
 * - 61  -> "1m 1s"
 * - 3600 -> "1h"
 * - 3661 -> "1h 1m 1s"
 */
export function duration(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "–";

  const total = Math.round(seconds);
  if (total < 60) return `${total}s`;

  const hrs = Math.floor(total / 3600);
  const remAfterHrs = total % 3600;
  const mins = Math.floor(remAfterHrs / 60);
  const secs = remAfterHrs % 60;

  const parts: string[] = [];
  if (hrs > 0) parts.push(`${hrs}h`);
  if (mins > 0) parts.push(`${mins}m`);
  if (secs > 0) parts.push(`${secs}s`);

  return parts.join(" ");
}

/**
 * For "Traffic: +38s" etc.
 */
export function signedDelay(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return "–";
  if (seconds === 0) return "+0s";
  return `+${duration(seconds)}`;
}

export function displayMinutes(seconds: number) {
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  return Math.round(seconds / 60);
}

/**
 * Returns local time formatted for <input type="datetime-local" />
 * Format: "YYYY-MM-DDTHH:mm"
 */
export function getNowForDateTimeLocal(): string {
  const now = new Date();

  // Round down to the minute (datetime-local doesn't support seconds well)
  now.setSeconds(0, 0);

  const pad = (n: number) => String(n).padStart(2, "0");

  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(
    now.getHours(),
  )}:${pad(now.getMinutes())}`;
}

/**
 * Parses "YYYY-MM-DDTHH:mm" as a LOCAL Date (not UTC).
 * Returns null if invalid.
 */
export function parseDateTimeLocal(value: string): Date | null {
  if (!value) return null;

  const [datePart, timePart] = value.split("T");
  if (!datePart || !timePart) return null;

  const [y, m, d] = datePart.split("-").map(Number);
  const [hh, mm] = timePart.split(":").map(Number);

  if (![y, m, d, hh, mm].every(Number.isFinite)) return null;

  const dt = new Date(y, m - 1, d, hh, mm, 0, 0);

  // Guard against JS Date overflow weirdness (e.g. month 13)
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== m - 1 ||
    dt.getDate() !== d ||
    dt.getHours() !== hh ||
    dt.getMinutes() !== mm
  ) {
    return null;
  }

  return dt;
}

/**
 * True if the provided datetime-local string is before "now" (rounded to the minute).
 */
export function isPastDateTimeLocal(value: string, now: Date = new Date()): boolean {
  const dt = parseDateTimeLocal(value);
  if (!dt) return false;

  const roundedNow = new Date(now);
  roundedNow.setSeconds(0, 0);

  return dt.getTime() < roundedNow.getTime();
}
